import type React from 'react';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UserItemActions from 'app/shared/flux/actions/UserItemActions';
import {
  emitTrackSaveHomeClickEvent,
  emitTrackUnSaveHomeClickEvent,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import { listingUtils_checkFavorite } from 'app/shared/utils/listingUtils';
import { getListingIdentifier } from 'app/shared/utils/listingsReducerUtils';
import { triggerCannonConfetti } from 'app/shared/utils/confetti';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';

const fireConfetti = (buttonRef?: React.RefObject<HTMLButtonElement | null>) => {
  const rect = buttonRef?.current?.getBoundingClientRect();
  triggerCannonConfetti({
    origin: {
      x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
      y: rect ? rect.top + rect.height / 2 : window.innerHeight,
    },
  });
};

export const useListingSaveActions = (buttonRef?: React.RefObject<HTMLButtonElement | null>) => {
  const dispatch = useDispatch();
  const pendingRef = useRef(false);
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails.currentListing);
  const isLoggedIn = useSelector((state: RootReduxState) => Boolean(state.user.loggedIn || state.user.info?.email));

  const listingId = getListingIdentifier(currentListing);
  const listingSaved = useSelector((state: RootReduxState) => {
    if (!currentListing) return false;
    const found = state.listings.listingGroups.byCoords?.find((l) => getListingIdentifier(l) === listingId);
    return listingUtils_checkFavorite((found ?? currentListing) as unknown as ListingDetails);
  });

  const toggleSave = () => {
    if (!currentListing || pendingRef.current) return;
    pendingRef.current = true;
    requestAnimationFrame(() => {
      pendingRef.current = false;
    });

    if (!listingSaved) {
      emitTrackSaveHomeClickEvent({
        listing: currentListing as unknown as ListingDetails,
        triggerLocation: 'home_details_halo',
        triggerObject: 'home_details_component|action_bar',
      });
    } else {
      emitTrackUnSaveHomeClickEvent({
        listing: currentListing as unknown as ListingDetails,
        triggerLocation: 'home_details_halo',
        triggerObject: 'home_details_component|action_bar',
      });
    }

    if (!listingSaved && isLoggedIn) {
      fireConfetti(buttonRef);
    }
    dispatch(UserItemActions.toggleUserItem('favorite', currentListing));
  };

  return { listingSaved, toggleSave };
};
